/**
 * ENG-3772 — pacing gate for LiveKit local-connection restarts.
 *
 * `LiveKitImpl.restartLocalConnection()` was stubbed to an unconditional
 * `return;` by PR #462 ("stability", commit e5a2a96bd) because the newts
 * restore handler can fire on every unhealthy→healthy transition (see
 * newts/connection.ts `#resetInactiveTimeout`): under a flapping transport
 * that tore the local peer connection down faster than it could rebuild —
 * a restart storm. The stub traded the storm for a dead restore path: a
 * wedged local connection was never rebuilt and sources disappeared
 * mid-session.
 *
 * This gate reinstates the restart while keeping the storm out. It reuses
 * the per-key exponential-backoff shape shipped for stall-driven
 * subscription resets in the July bandwidth-governor work (PR #1235,
 * `#stallResets` in livekit.ts): the first attempt runs immediately,
 * consecutive attempts double the required spacing up to a ceiling, and a
 * long quiet stretch decays back to the base interval because a later
 * restart is a new incident, not a continuation of the old one.
 *
 * Pure policy module with no LiveKit imports, mirroring
 * `bandwidth-governor.ts`. Per rule: think-in-systems (ai.knowledge/global)
 * — pacing policy is separated from the transport it paces. Per rule:
 * reference-rules-in-comments (ai.knowledge/global).
 */

/**
 * Restore handlers fire far more often than a stop+start cycle can finish
 * when the transport flaps. 5s base spacing lets one full local rebuild
 * complete before the next may begin; 60s caps the worst-case spacing so a
 * genuinely wedged connection still recovers; 2 minutes of quiet forgives
 * the backoff history entirely.
 */
export const LOCAL_RESTART_BASE_INTERVAL_MS = 5_000;
export const LOCAL_RESTART_MAX_INTERVAL_MS = 60_000;
export const LOCAL_RESTART_BACKOFF_DECAY_MS = 120_000;

interface RestartRecord {
  lastAttemptAt: number;
  nextIntervalMs: number;
}

export interface RestartBackoffGateOptions {
  baseIntervalMs?: number;
  maxIntervalMs?: number;
  decayMs?: number;
}

/** Per-key exponential-backoff gate. Keys are agent id hex strings. */
export class RestartBackoffGate {
  readonly #baseIntervalMs: number;
  readonly #maxIntervalMs: number;
  readonly #decayMs: number;

  #attempts = new Map<string, RestartRecord>();

  public constructor(options: RestartBackoffGateOptions = {}) {
    this.#baseIntervalMs = options.baseIntervalMs ?? LOCAL_RESTART_BASE_INTERVAL_MS;
    this.#maxIntervalMs = options.maxIntervalMs ?? LOCAL_RESTART_MAX_INTERVAL_MS;
    this.#decayMs = options.decayMs ?? LOCAL_RESTART_BACKOFF_DECAY_MS;
  }

  /**
   * Ask the gate whether a restart may run now. A `true` result CONSUMES the
   * attempt (the caller must actually restart); `false` means the caller must
   * skip — the interval since the previous attempt is still inside the
   * backoff window. Same advance-only-on-execute contract as the stall-reset
   * pacing this mirrors: rejected attempts do not widen the window further.
   */
  public tryAcquire(key: string, now: number = Date.now()): boolean {
    const prior = this.#attempts.get(key);

    // A quiet stretch longer than the decay window means the last incident
    // resolved; restore the fast first response instead of punishing the
    // next unrelated restart.
    const consecutive = prior !== undefined && now - prior.lastAttemptAt < this.#decayMs;
    const intervalMs = consecutive ? prior.nextIntervalMs : this.#baseIntervalMs;

    if (prior && now - prior.lastAttemptAt < intervalMs) {
      return false;
    }

    this.#attempts.set(key, {
      lastAttemptAt: now,
      // Doubling applies only to consecutive attempts; a fresh incident
      // restarts the ladder at the base interval.
      nextIntervalMs: consecutive
        ? Math.min(intervalMs * 2, this.#maxIntervalMs)
        : this.#baseIntervalMs
    });

    return true;
  }

  /** Drop all history (e.g. when the owning connection is torn down). */
  public clear(): void {
    this.#attempts.clear();
  }
}
